import { useEffect, useState, ChangeEvent, FormEvent } from "react";

import Modal from "../../tailadmin/components/ui/Modal";
import BottomModal from "../ui/BottomModal";
import InputDefault from "../ui/InputDefault";

import exercicies from "../../services/exercicies";
import { useAuth } from "../../context/AuthContext";
import { usePrescription } from "../../context/PrescriptionContext";

import { Exercise, ExerciseInPlan } from "../../types";

interface PrescriptionExercicieModalProps {
  isOpen: boolean;
  data: Partial<ExerciseInPlan> | null;
  onBack: () => void;
}

/**
 * Estado do formulário usado no modal.
 * Mantém compatibilidade com o backend que espera reps/sets.
 */
interface ModalFormState extends Omit<Partial<Exercise>, "id"> {
  id: string;
  reps?: number | string;
  sets?: number | string;
  interval?: number | string;
  charge?: number | string;
}

function PrescriptionExercicieModal({
  isOpen,
  data,
  onBack,
}: PrescriptionExercicieModalProps) {
  const [form, setForm] = useState<ModalFormState>({
    id: "",
    reps: "",
    sets: "",
    interval: "",
    charge: "",
  });

  const [exercicieslist, setExercicieslist] = useState<Exercise[]>([]);

  const { getUser } = useAuth();
  const { saveExercicie } = usePrescription();

  const loadExercicies = async () => {
    const userId = getUser()?.id;
    if (!userId) return;

    const response = await exercicies.list_all(String(userId), 1, 10);
    setExercicieslist(response);
  };

  useEffect(() => {
    if (!isOpen) return;

    loadExercicies();

    if (data) {
      setForm({
        ...data,
        id: data.id ? String(data.id) : "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    saveExercicie({ ...form, id: Number(form.id) });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onBack}
      title="Exercício"
      className="!w-full !max-w-[600px]"
    >
      <div>
        <form onSubmit={handleSubmit} className="grid w-full p-4">
          <div className="w-full">
            {/* Exercício */}
            <div className="w-full grid gap-2 mt-4">
              <label
                htmlFor="id"
                className="pl-1 text-md font-medium text-black dark:text-white"
              >
                Exercício
              </label>

              <div className="relative z-20">
                <select
                  id="id"
                  name="id"
                  required
                  value={form.id || ""}
                  onChange={handleChange}
                  className="w-full appearance-none rounded border border-stroke bg-transparent py-3 px-5 outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input text-black dark:text-white"
                >
                  <option value="" disabled>
                    Selecione um exercício
                  </option>

                  {exercicieslist.map((exercise) => (
                    <option key={exercise.id} value={exercise.id}>
                      {exercise.name}
                    </option>
                  ))}
                </select>

                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                  <svg
                    className="fill-current"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.293 8.293a1 1 0 0 1 1.414 0L12 13.586l5.293-5.293a1 1 0 1 1 1.414 1.414l-6 6a1 1 0 0 1-1.414 0l-6-6a1 1 0 0 1 0-1.414z"
                    />
                  </svg>
                </span>
              </div>
            </div>

            {/* Reps / Sets */}
            <div className="mt-4 flex flex-col gap-4 sm:flex-row">
              <InputDefault
                label="Repetições"
                id="reps"
                name="reps"
                type="number"
                value={form.reps}
                onChange={handleChange}
              />

              <InputDefault
                label="Séries"
                id="sets"
                name="sets"
                type="number"
                value={form.sets}
                onChange={handleChange}
              />
            </div>

            {/* Intervalo / Carga */}
            <div className="mt-4 flex flex-col gap-4 sm:flex-row">
              <InputDefault
                label="Intervalo"
                id="interval"
                name="interval"
                type="text"
                value={form.interval}
                onChange={handleChange}
              />

              <InputDefault
                label="Carga"
                id="charge"
                name="charge"
                type="text"
                value={form.charge}
                onChange={handleChange}
              />
            </div>
          </div>
        </form>

        <div className="p-4 pb-2">
          <BottomModal
            onBack={onBack}
            id={form.id}
            onSave={() => saveExercicie({ ...form, id: Number(form.id) })}
          />
        </div>
      </div>
    </Modal>
  );
}

export default PrescriptionExercicieModal;
